import React, { useState, useEffect } from 'react';
import { Modal, Button, Container, Row, Col, ProgressBar, Form, Table, Alert, Badge } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight, FaCheck, FaPlus, FaTrash } from 'react-icons/fa';
import CreateClassesStep from './CreateClassesStep';
import StudentSelectionStep from './StudentSelectionStep';
import StudentDecisionStep from './StudentDecisionStep';
import ConfirmationStep from './ConfirmationStep';
import PopulationMethodStep from './PopulationMethodStep';

const ClassTransitionWorkflowModal = ({ 
  show, 
  onHide, 
  schoolid, 
  getTeachersBySchoolId, 
  getStudentsBySchool, 
  getClassesBySchoolId,
  createBulkClasses,
  addStudents,
  bulkUpdateStudentClassId,
  markStudentAsRepeat,
  batchDeleteStudents,
  teacherid,
  teacherName
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [workflowData, setWorkflowData] = useState({
    createdClasses: [],
    populationMethod: null, // 'simple', 'complex', or 'new'
    newStudents: [], // for new students
    selectedStudents: [], // for existing students
    sourceClasses: [], // for simple/hybrid transitions - which classes to move from
    classMappings: {}, // for simple transitions - maps sourceClassId to targetClassId
    studentDecisions: {}, // studentId: { action: 'move'|'repeat'|'remove', targetClassId: null }
    // Add API functions to workflow data so steps can access them
    getTeachersBySchoolId: getTeachersBySchoolId,
    getStudentsBySchool: getStudentsBySchool,
    getClassesBySchoolId: getClassesBySchoolId,
    createBulkClasses: createBulkClasses,
    addStudents: addStudents,
    bulkUpdateStudentClassId: bulkUpdateStudentClassId,
    markStudentAsRepeat: markStudentAsRepeat,
    batchDeleteStudents: batchDeleteStudents,
    teacherid: teacherid,
    teacherName: teacherName,
  });

  // Update workflowData when props change
  useEffect(() => {
    setWorkflowData(prev => ({
      ...prev,
      getTeachersBySchoolId: getTeachersBySchoolId,
      getStudentsBySchool: getStudentsBySchool,
      getClassesBySchoolId: getClassesBySchoolId,
      createBulkClasses: createBulkClasses,
      addStudents: addStudents,
      bulkUpdateStudentClassId: bulkUpdateStudentClassId,
      markStudentAsRepeat: markStudentAsRepeat,
      batchDeleteStudents: batchDeleteStudents,
      teacherid: teacherid,
      teacherName: teacherName,
    }));
  }, [getTeachersBySchoolId, getStudentsBySchool, getClassesBySchoolId, createBulkClasses, addStudents, bulkUpdateStudentClassId, markStudentAsRepeat, batchDeleteStudents, teacherid, teacherName]);

  const steps = [
    { id: 1, name: 'Create Classes', description: 'Set up new target classes' },
    { id: 2, name: 'Transition Type', description: 'Choose transition approach' },
    { id: 3, name: 'Student Selection', description: 'Select students to process' },
    { id: 4, name: 'Student Decisions', description: 'Decide fate of each student' },
    { id: 5, name: 'Confirmation', description: 'Review and execute changes' }
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    // Reset state
    setCurrentStep(1);
    setWorkflowData({
      createdClasses: [],
      populationMethod: null,
      newStudents: [],
      selectedStudents: [],
      sourceClasses: [],
      classMappings: {},
      studentDecisions: {},
      getTeachersBySchoolId: getTeachersBySchoolId,
      getStudentsBySchool: getStudentsBySchool,
      getClassesBySchoolId: getClassesBySchoolId,
      createBulkClasses: createBulkClasses,
      addStudents: addStudents,
      bulkUpdateStudentClassId: bulkUpdateStudentClassId,
      markStudentAsRepeat: markStudentAsRepeat,
      batchDeleteStudents: batchDeleteStudents,
      teacherid: teacherid,
      teacherName: teacherName,
    });
    onHide();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <CreateClassesStep 
          workflowData={workflowData} 
          setWorkflowData={setWorkflowData}
          schoolid={schoolid}
        />;
      case 2:
        return <PopulationMethodStep 
          workflowData={workflowData} 
          setWorkflowData={setWorkflowData}
        />;
      case 3:
        return <StudentSelectionStep 
          workflowData={workflowData} 
          setWorkflowData={setWorkflowData}
          schoolid={schoolid}
        />;
      case 4:
        return <StudentDecisionStep 
          workflowData={workflowData} 
          setWorkflowData={setWorkflowData}
        />;
      case 5:
        return <ConfirmationStep 
          workflowData={workflowData} 
          setWorkflowData={setWorkflowData}
          onComplete={handleClose}
        />;
      default:
        return <div>Invalid step</div>;
    }
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        // Check if we have valid classes with all required fields
        const validClasses = workflowData.createdClasses.filter(c => 
          c.isValid && c.teacherId && c.classOwnerId && !c.isDuplicate
        );
        return validClasses.length === 0;
      case 2:
        return !workflowData.populationMethod;
      case 3:
        // Different validation based on transition type
        switch (workflowData.populationMethod) {
          case 'new':
            // Check if we have valid new students
            const validNewStudents = (workflowData.newStudents || []).filter(s => s.isValid);
            return validNewStudents.length === 0;
          case 'simple':
            // Check if we have class mappings created
            return Object.keys(workflowData.classMappings || {}).length === 0;
          case 'complex':
            // Check if we have selected existing students  
            return (workflowData.selectedStudents || []).length === 0;
          default:
            return true;
        }
      case 4:
        // Check if all students have valid decisions
        let studentsToProcess = [];
        
        switch (workflowData.populationMethod) {
          case 'new':
            studentsToProcess = (workflowData.newStudents || []).filter(s => s.isValid);
            break;
          case 'simple':
            // For simple transitions, we might auto-assign students
            // But still need decisions for edge cases
            studentsToProcess = (workflowData.selectedStudents || []);
            break;
          case 'complex':
            studentsToProcess = (workflowData.selectedStudents || []);
            break;
          default:
            return true;
        }
        
        if (studentsToProcess.length === 0) return false; // No students to process
        
        const decisions = workflowData.studentDecisions || {};
        
        // Check if all students have decisions
        const allHaveDecisions = studentsToProcess.every(student => 
          decisions[student.id] && decisions[student.id].action
        );
        
        if (!allHaveDecisions) return true;
        
        // Check if all decisions are valid
        const allDecisionsValid = studentsToProcess.every(student => {
          const decision = decisions[student.id];
          if (!decision || !decision.action) return false;
          
          switch (decision.action) {
            case 'move':
              return !!decision.targetClassId;
            case 'repeat':
              return !!decision.repeatYear && !!decision.reason?.trim();
            case 'remove':
              return !!decision.reason?.trim();
            default:
              return false;
          }
        });
        
        return !allDecisionsValid;
      default:
        return false;
    }
  };

  const currentStepData = steps.find(step => step.id === currentStep);
  const progressPercentage = (currentStep / steps.length) * 100;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Class Transition Workflow
          {currentStepData && (
            <div className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>
              Step {currentStep} of {steps.length}: {currentStepData.name}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Progress Bar */}
        <div className="mb-4">
          <ProgressBar 
            now={progressPercentage} 
            label={`Step ${currentStep} of ${steps.length}`}
            variant="primary"
          />
          <div className="d-flex justify-content-between mt-2">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={`text-center ${step.id === currentStep ? 'text-primary' : step.id < currentStep ? 'text-success' : 'text-muted'}`}
                style={{ fontSize: '0.75rem', flex: 1 }}
              >
                <div className="fw-bold">{step.name}</div>
                <div>{step.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Container fluid>
          {renderStepContent()}
        </Container>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button 
            variant="secondary" 
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            <FaArrowLeft className="me-2" />
            Previous
          </Button>
          
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            
            {currentStep < steps.length ? (
              <Button 
                variant="primary" 
                onClick={handleNext}
                disabled={isNextDisabled()}
              >
                Next
                <FaArrowRight className="ms-2" />
              </Button>
            ) : (
              <Button 
                variant="success" 
                onClick={handleClose}
                disabled={isNextDisabled()}
              >
                <FaCheck className="me-2" />
                Complete
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ClassTransitionWorkflowModal;